import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Popover from '@material-ui/core/Popover';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles } from '@material-ui/core/styles';
import { Panel, Badge } from './shared';
import { formatCount, formatMetric, isOverAssigned } from '../utils';

const useStyles = makeStyles(theme => ({
  card: { position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 180ms ease-out, border-color 180ms ease-out' },
  clickable: { cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)', borderColor: theme.palette.primary.main }, '&:focus': { outline: `3px solid ${theme.palette.primary.main}44`, outlineOffset: 2 } },
  label: { color: 'var(--dm-muted, #647184)', fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.4 },
  value: { fontSize: '1.9rem', fontWeight: 780, lineHeight: 1.2, marginTop: theme.spacing(1), letterSpacing: '-.035em', overflowWrap: 'anywhere', [theme.breakpoints.down('xs')]: { fontSize: '1.65rem', marginTop: theme.spacing(0.75) } },
  description: { color: 'var(--dm-muted, #647184)', fontSize: '0.72rem', lineHeight: 1.45, marginTop: theme.spacing(0.75) },
  trend: { display: 'flex', alignItems: 'center', gap: 4, marginTop: theme.spacing(1), fontSize: '0.74rem', lineHeight: 1.35, fontWeight: 600 },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 'auto', paddingTop: theme.spacing(1.25), [theme.breakpoints.down('xs')]: { alignItems: 'flex-start' } },
  how: { fontSize: '0.72rem', fontWeight: 600 },
  drill: { display: 'inline-flex', alignItems: 'center', fontSize: '0.72rem', fontWeight: 700, color: theme.palette.primary.main },
  popover: { maxWidth: 340, padding: theme.spacing(1.5, 2) },
  formula: { fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5, marginTop: theme.spacing(0.75), whiteSpace: 'pre-wrap' },
}));

const TREND_ICONS = { up: TrendingUpIcon, down: TrendingDownIcon, flat: TrendingFlatIcon };
const TREND_COLORS = { up: '#15803d', down: '#b91c1c', flat: 'var(--dm-muted, #647184)' };

export const trendText = trend => (
  trend
    ? `${formatCount(trend.current)} new vs ${formatCount(trend.previous)} in the ${trend.period_label} before`
    : 'Current total'
);

const KpiCard = ({ item, onDrilldown }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const trend = item.trend;
  const TrendIcon = TREND_ICONS[trend?.direction] || TrendingFlatIcon;
  const clickable = !!(item.drilldown?.api || item.drilldown?.anchor);
  const over = item.unit === 'percent' && isOverAssigned(item.value);
  const open = () => clickable && onDrilldown(item.drilldown);
  const stop = event => event.stopPropagation();
  return (
    <Panel
      className={`${classes.card} ${clickable ? classes.clickable : ''}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `${item.label}: ${formatMetric(item.value, item.unit)}. ${item.drilldown.label || 'Open'}` : undefined}
      onClick={clickable ? open : undefined}
      onKeyDown={clickable ? event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); open(); } } : undefined}
      data-testid={`kpi-${item.key}`}
    >
      <Typography className={classes.label}>{item.label}</Typography>
      <Typography className={classes.value} style={over ? { color: '#b91c1c' } : undefined}>{formatMetric(item.value, item.unit)}</Typography>
      {over ? <Badge tone="red">Over-assigned</Badge> : null}
      <div className={classes.trend} style={{ color: trend ? TREND_COLORS[trend.direction] || TREND_COLORS.flat : TREND_COLORS.flat }}>
        {trend ? <TrendIcon fontSize="small" aria-label={trend.direction} /> : null}
        <span>{trendText(trend)}</span>
      </div>
      {item.description ? <Typography className={classes.description}>{item.description}</Typography> : null}
      <div className={classes.footer}>
        {item.formula ? (
          <Link
            component="button"
            type="button"
            className={classes.how}
            onClick={event => { stop(event); setAnchorEl(event.currentTarget); }}
            onKeyDown={stop}
            aria-haspopup="dialog"
          >How is this calculated?</Link>
        ) : <span />}
        {clickable ? <span className={classes.drill}>{item.drilldown.label}<ChevronRightIcon fontSize="small" /></span> : null}
      </div>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={stop}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={classes.popover}>
          <Typography style={{ fontWeight: 700, fontSize: '0.84rem' }}>{item.label}</Typography>
          <Typography className={classes.formula}>{item.formula}</Typography>
        </div>
      </Popover>
    </Panel>
  );
};

const KpiGrid = ({ items = [], onDrilldown }) => (
  <Grid container spacing={2} style={{ marginTop: 16 }} aria-label="Platform key performance indicators">
    {items.map(item => <Grid item xs={12} sm={6} lg={3} key={item.key}><KpiCard item={item} onDrilldown={onDrilldown} /></Grid>)}
  </Grid>
);

export default KpiGrid;
